import { useState } from "react";
import { supabase } from "../supabase";
import { mc, effectiveRetta } from "../utils/helpers";

// ── PALETTE (copia locale — identica a App.js) ────────────────────────────────
const P = {
  bg:"#080B14", card:"#0D1020", border:"#1D2238",
  gold:"#E9622A", goldGlow:"rgba(233,98,42,0.12)", goldBorder:"rgba(233,98,42,0.30)",
  text:"#EBE8F5", muted:"#8A8EAE", secondary:"#B4B7D0",
  green:"#5BB87A", yellow:"#E9622A", red:"#C46B6B", blue:"#5A82B8", purple:"#a78bfa",
  sidebar:"#060810", card2:"#111525", card3:"#161B2E",
  borderSubtle:"#141829", borderStrong:"#2A3055"
};

// ── PROPS ─────────────────────────────────────────────────────────────────────
// allievi           : array
// setAllievi        : fn(updater)
// maestriColors     : object
// corsi             : array  — [{ id, nome, maestro, label }]  (può essere vuoto)
// isMobile          : bool
// getRettaPagata    : fn(id) → bool
// nextId            : number
// setNextId         : fn(updater)
// setConfirmDelete  : fn(allievo)
// haStorico         : fn(id) → bool — presenze o rette registrate
// eliminaAllievoDefinitivo: fn(id) — solo archiviati senza storico
// setPresAllievoFrom: fn(string)
// setPresAllievo    : fn(id)
// goTab             : fn(id)
// notify            : fn(msg, tipo)

export default function DatabaseAllievi({
  allievi, setAllievi, maestriColors, corsi = [], isMobile,
  getRettaPagata, nextId, setNextId, setConfirmDelete, ripristinaAllievo,
  haStorico, eliminaAllievoDefinitivo,
  setPresAllievoFrom, setPresAllievo, setTab, notify, updateAllievoRettaBase
}) {
  const [dbSearch, setDbSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showArchiviati, setShowArchiviati] = useState(false);
  const [confirmRipristino, setConfirmRipristino] = useState(null); // allievo da ripristinare
  const [confirmElimina, setConfirmElimina] = useState(null); // archiviato senza storico da eliminare
  const [draftRetta, setDraftRetta] = useState({}); // { [id]: valoreInEdit }
  const FORM_VUOTO = {nome:'',cognome:'',telefono:'',email:'',data_nascita:'',strumento:'',retta:120,note:'',corsoId:'',maestro:''};
  const [dbForm, setDbForm] = useState(FORM_VUOTO);

  const f = (k) => (e) => setDbForm(p => ({...p, [k]: e.target.value}));

  const onCorsoChange = (e) => {
    const id = e.target.value;
    const corso = corsi.find(c => String(c.id) === String(id));
    setDbForm(p => ({
      ...p,
      corsoId: id,
      maestro: corso ? corso.maestro : p.maestro,
    }));
  };

  const onModalKey = (e) => {
    if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA' && e.target.tagName !== 'BUTTON') {
      e.preventDefault();
      save();
    }
  };

  const openAdd = () => { setDbForm(FORM_VUOTO); setShowModal(true); };

  const openProfilo = (id) => {
    setPresAllievoFrom('db-allievi');
    setPresAllievo(id);
    setTab('presenze');
  };

  const save = async () => {
    if (!dbForm.nome.trim()) { notify('Il nome è obbligatorio', 'err'); return; }

    const nomeCompleto = `${dbForm.nome} ${dbForm.cognome}`.trim();
    const retta        = Number(dbForm.retta) || 120;
    const maestro      = dbForm.maestro || '';
    const id           = nextId;
    setNextId(id + 1);

    const nuovoAllievo = {
      id,
      nome:             nomeCompleto,
      maestro,
      retta,
      note:             dbForm.note || '',
      dataNascita:      dbForm.data_nascita || null,
      telefono:         dbForm.telefono || '',
      email:            dbForm.email    || '',
      rettaPagata:      false,
      iscrPagata:       false,
      trattamento:      'normale',
      metodoPagamento:  'contanti',
      specNoRetta:      false,
      specNoIscrizione: false,
      specPrezzoCustom: null,
      corsi: [{ maestro, strumento: dbForm.strumento || '', retta, quota_m: null }],
      ...(dbForm.corsoId ? { corsoId: dbForm.corsoId } : {}),
    };

    const { error } = await supabase.from('allievi').insert({
      id,
      nome:               nomeCompleto,
      maestro,
      retta,
      iscrizione:         20,
      note:               nuovoAllievo.note,
      telefono:           nuovoAllievo.telefono,
      email:              nuovoAllievo.email,
      data_nascita:       nuovoAllievo.dataNascita,
      retta_pagata:       false,
      iscr_pagata:        false,
      trattamento:        'normale',
      metodo_pagamento:   'contanti',
      spec_no_retta:      false,
      spec_no_iscrizione: false,
      spec_prezzo_custom: null,
      corsi:              nuovoAllievo.corsi,
      lezioni_maestro:    {},
      corso_id:           dbForm.corsoId ? Number(dbForm.corsoId) : null,
    });

    if (error) { notify('Errore salvataggio: ' + error.message, 'err'); return; }
    setAllievi(prev => [...prev, nuovoAllievo]);
    setShowModal(false);
    notify('Allievo aggiunto ✓', 'ok');
    openProfilo(id);
  };

  // Archiviato = ha un periodo di pausa ancora aperto (senza data di rientro)
  const archiviato = (a) => (a.pause || []).some(p => !p.al);
  const dataArch   = (a) => ((a.pause || []).find(p => !p.al) || {}).dal || null;
  const nArchiviati = allievi.filter(archiviato).length;

  const filtrati = allievi
    .filter(a => showArchiviati ? archiviato(a) : !archiviato(a))
    .filter(a => `${a.nome} ${a.note}`.toLowerCase().includes(dbSearch.toLowerCase()))
    .sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'it'));

  // "22/09/2026" da "2026-09-22"
  const fmtData = (iso) => { const [y,m,d] = String(iso).split('-'); return `${d}/${m}/${y}`; };

  const statoColor = (s) => s==='pagato' ? P.green : s==='parziale' ? P.yellow : P.red;
  const statoLabel = (s) => s==='pagato' ? 'Pagato' : s==='parziale' ? 'Parziale' : 'Non pagato';

  return (
    <div style={{animation:'fi .35s ease'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:26,flexWrap:'wrap',gap:12}}>
        <div>
          <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:11,letterSpacing:3,color:P.muted,marginBottom:7,textTransform:'uppercase'}}>ANAGRAFICA</div>
          <h1 style={{fontFamily:"'Syne',sans-serif",fontSize:isMobile?22:32,fontWeight:800,letterSpacing:-0.5}}>Database Allievi</h1>
        </div>
        <button onClick={openAdd} style={{background:P.gold,color:'#fff',border:'none',padding:'10px 20px',borderRadius:8,cursor:'pointer',fontWeight:600,fontSize:15}}>+ Aggiungi</button>
      </div>

      <div style={{display:'flex',alignItems:'center',gap:12,flexWrap:'wrap',marginBottom:16}}>
        <input placeholder="Cerca per nome o note..." value={dbSearch} onChange={e=>setDbSearch(e.target.value)}
          style={{background:P.card,border:`1px solid ${P.border}`,color:P.text,padding:'9px 15px',borderRadius:8,width:'100%',maxWidth:320,fontSize:15,fontFamily:"'Syne',sans-serif"}}/>
        {(nArchiviati > 0 || showArchiviati) && (
          <button onClick={()=>setShowArchiviati(v=>!v)}
            style={{padding:'9px 14px',borderRadius:8,cursor:'pointer',fontSize:14,fontFamily:"'Syne',sans-serif",
              border:`1px solid ${showArchiviati?P.gold:P.border}`,
              background:showArchiviati?`${P.gold}18`:'transparent',
              color:showArchiviati?P.gold:P.muted}}>
            {showArchiviati ? '← Torna agli attivi' : `Mostra archiviati (${nArchiviati})`}
          </button>
        )}
      </div>

      {showArchiviati && (
        <div style={{fontSize:14,color:P.muted,marginBottom:12,lineHeight:1.5}}>
          Questi allievi non compaiono più nelle liste, ma presenze e pagamenti sono conservati
          e continuano a contare nei mesi precedenti all'archiviazione.
          Si possono eliminare definitivamente solo quelli senza presenze né rette registrate.
        </div>
      )}

      <div style={{background:P.card,border:`1px solid ${P.border}`,borderRadius:14,overflowX:'auto'}}>
        <table style={{width:'100%',borderCollapse:'collapse',minWidth:600}}>
          <thead>
            <tr style={{borderBottom:`1px solid ${P.border}`}}>
              {['#','Nome','Telefono','Email','Retta','Stato',''].map(h=>(
                <th key={h} style={{padding:'12px 14px',textAlign:'left',fontFamily:"'IBM Plex Mono',monospace",fontSize:11,letterSpacing:2,color:P.muted,fontWeight:400}}>{h.toUpperCase()}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtrati.map((a,i)=>{
              const c = mc(a.maestro, maestriColors);
              const pagato = getRettaPagata(a.id) ? 'pagato' : 'non_pagato';
              return (
                <tr key={a.id} style={{borderBottom:i<filtrati.length-1?`1px solid ${P.border}`:'none',cursor:'pointer'}}
                  onClick={()=>openProfilo(a.id)}
                  onMouseEnter={e=>e.currentTarget.style.background=`${c}10`}
                  onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                  <td style={{padding:'10px 14px',color:P.muted,fontSize:14}}>{i+1}</td>
                  <td style={{padding:'10px 14px'}}>
                    <div style={{fontSize:15,fontWeight:500,color:archiviato(a)?P.muted:P.text}}>{a.nome}</div>
                    <div style={{fontSize:12,color:P.muted,marginTop:2}}>
                      {a.maestro}{a.dataNascita ? ` · ${a.dataNascita}` : ''}
                      {archiviato(a) ? ` · archiviato il ${fmtData(dataArch(a))}` : ''}
                    </div>
                  </td>
                  <td style={{padding:'10px 14px',fontSize:14,color:P.muted}}>{a.telefono||'—'}</td>
                  <td style={{padding:'10px 14px',fontSize:14,color:P.muted}}>{a.email||'—'}</td>
                  <td style={{padding:'10px 14px'}} onClick={e=>e.stopPropagation()}>
                    <div style={{display:'inline-flex',alignItems:'center',gap:2,
                      border:`1px solid ${P.border}`,borderRadius:7,padding:'4px 8px',background:P.bg}}>
                      <span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:14,color:P.secondary}}>€</span>
                      <input
                        type="number" min="0" step="1"
                        value={draftRetta[a.id] !== undefined ? draftRetta[a.id] : effectiveRetta(a)}
                        onChange={e=>setDraftRetta(prev=>({...prev,[a.id]:e.target.value}))}
                        onBlur={e=>{
                          const v=Number(e.target.value);
                          if(!isNaN(v) && v!==effectiveRetta(a)){ updateAllievoRettaBase(a.id, v); notify('Retta aggiornata ✓','ok'); }
                          setDraftRetta(prev=>{const n={...prev};delete n[a.id];return n;});
                        }}
                        onKeyDown={e=>e.key==='Enter'&&e.target.blur()}
                        style={{width:56,background:'transparent',border:'none',
                          fontFamily:"'IBM Plex Mono',monospace",fontSize:15,fontWeight:600,
                          color:P.gold,outline:'none',padding:0}}/>
                    </div>
                  </td>
                  <td style={{padding:'10px 14px'}}>
                    <span style={{fontSize:12,padding:'3px 9px',borderRadius:20,fontWeight:600,
                      background:`${statoColor(pagato)}18`,color:statoColor(pagato),border:`1px solid ${statoColor(pagato)}44`}}>
                      {statoLabel(pagato)}
                    </span>
                  </td>
                  <td style={{padding:'10px 14px'}} onClick={e=>e.stopPropagation()}>
                    {archiviato(a) ? (
                      <div style={{display:'flex',gap:6}}>
                        <button onClick={()=>setConfirmRipristino(a)} title="Ripristina allievo"
                          style={{padding:'4px 10px',borderRadius:6,border:`1px solid ${P.green}40`,background:`${P.green}12`,color:P.green,cursor:'pointer',fontSize:13,whiteSpace:'nowrap'}}>
                          ↩ Ripristina
                        </button>
                        {!haStorico(a.id) && (
                          <button onClick={()=>setConfirmElimina(a)} title="Elimina definitivamente"
                            style={{padding:'4px 10px',borderRadius:6,border:`1px solid ${P.red}30`,background:`${P.red}12`,color:P.red,cursor:'pointer',fontSize:13,whiteSpace:'nowrap'}}>
                            Elimina
                          </button>
                        )}
                      </div>
                    ) : (
                      <button onClick={()=>setConfirmDelete(a)} title="Archivia allievo"
                        style={{padding:'4px 10px',borderRadius:6,border:`1px solid ${P.red}30`,background:`${P.red}12`,color:P.red,cursor:'pointer',fontSize:13}}>✕</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {confirmRipristino && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.78)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center'}}
          onClick={e=>{if(e.target===e.currentTarget)setConfirmRipristino(null);}}>
          <div style={{background:P.card2||P.card,border:`1px solid ${P.border}`,borderRadius:18,padding:28,width:440,maxWidth:'94vw'}}>
            <div style={{fontFamily:"'Syne',sans-serif",fontSize:19,fontWeight:700,marginBottom:8}}>
              Ripristina {confirmRipristino.nome}
            </div>
            <p style={{fontSize:15,color:P.muted,marginBottom:20,lineHeight:1.6}}>
              Archiviato il {fmtData(dataArch(confirmRipristino))}. Come vuoi rimetterlo in elenco?
            </p>

            <button onClick={()=>{ripristinaAllievo(confirmRipristino.id,'riprende');setConfirmRipristino(null);}}
              style={{width:'100%',textAlign:'left',padding:'13px 15px',marginBottom:10,borderRadius:10,cursor:'pointer',
                border:`1px solid ${P.green}55`,background:`${P.green}12`,color:P.text,fontFamily:"'Syne',sans-serif"}}>
              <div style={{fontSize:15,fontWeight:600,color:P.green,marginBottom:3}}>Riprende da questo mese</div>
              <div style={{fontSize:13,color:P.muted,lineHeight:1.5}}>
                I mesi in cui è stato fermo restano fuori dai conti. Da usare quando l'allievo ha
                davvero sospeso e ora rientra.
              </div>
            </button>

            <button onClick={()=>{ripristinaAllievo(confirmRipristino.id,'annulla');setConfirmRipristino(null);}}
              style={{width:'100%',textAlign:'left',padding:'13px 15px',marginBottom:16,borderRadius:10,cursor:'pointer',
                border:`1px solid ${P.border}`,background:'transparent',color:P.text,fontFamily:"'Syne',sans-serif"}}>
              <div style={{fontSize:15,fontWeight:600,marginBottom:3}}>Annulla l'archiviazione</div>
              <div style={{fontSize:13,color:P.muted,lineHeight:1.5}}>
                Torna attivo anche nei mesi di pausa, come se non fosse mai stato archiviato.
                Da usare se l'avevi archiviato per errore.
              </div>
            </button>

            <button onClick={()=>setConfirmRipristino(null)}
              style={{width:'100%',padding:'10px',background:'transparent',border:`1px solid ${P.border}`,
                color:P.muted,borderRadius:8,cursor:'pointer',fontSize:15,fontFamily:"'Syne',sans-serif"}}>
              Annulla
            </button>
          </div>
        </div>
      )}

      {confirmElimina && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.78)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center'}}
          onClick={e=>{if(e.target===e.currentTarget)setConfirmElimina(null);}}>
          <div style={{background:P.card2||P.card,border:'1px solid rgba(180,60,60,0.35)',borderRadius:18,padding:28,width:440,maxWidth:'94vw'}}>
            <div style={{fontFamily:"'Syne',sans-serif",fontSize:19,fontWeight:700,marginBottom:8}}>
              Elimina {confirmElimina.nome}
            </div>
            <p style={{fontSize:15,color:P.muted,marginBottom:20,lineHeight:1.6}}>
              Non ha presenze né rette registrate, quindi i conti non cambiano.<br/>
              L'allievo viene cancellato dal database e non si può recuperare.
            </p>
            <div style={{display:'flex',gap:10}}>
              <button onClick={()=>setConfirmElimina(null)}
                style={{flex:1,padding:'11px',background:'transparent',border:`1px solid ${P.border}`,
                  color:P.muted,borderRadius:8,cursor:'pointer',fontSize:15,fontFamily:"'Syne',sans-serif"}}>
                Annulla
              </button>
              <button onClick={()=>{eliminaAllievoDefinitivo(confirmElimina.id);setConfirmElimina(null);}}
                style={{flex:1,padding:'11px',background:`${P.red}20`,border:`1px solid ${P.red}60`,
                  color:P.red,borderRadius:8,cursor:'pointer',fontWeight:600,fontSize:15,fontFamily:"'Syne',sans-serif"}}>
                Elimina definitivamente
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.6)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center'}}
          onClick={e=>{if(e.target===e.currentTarget)setShowModal(false);}}>
          <div onKeyDown={onModalKey} style={{background:P.card,border:`1px solid ${P.border}`,borderRadius:16,padding:28,width:420,maxWidth:'94vw',maxHeight:'90vh',overflowY:'auto'}}>
            <div style={{fontSize:17,fontWeight:600,marginBottom:18,color:P.text}}>Nuovo allievo</div>

            {corsi.length > 0 && (
              <div style={{marginBottom:12}}>
                <label style={{fontSize:12,color:P.muted,display:'block',marginBottom:4,letterSpacing:1,textTransform:'uppercase'}}>Corso</label>
                <select value={dbForm.corsoId} onChange={onCorsoChange}
                  style={{width:'100%',background:P.bg,border:`1px solid ${P.border}`,borderRadius:8,padding:'8px 10px',color:P.text,fontSize:15,cursor:'pointer'}}>
                  <option value="">— Nessun corso selezionato —</option>
                  {corsi.map(c => (
                    <option key={c.id} value={String(c.id)}>
                      {c.maestro ? `${c.nome} — ${c.maestro}` : c.nome}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div style={{marginBottom:12}}>
              <label style={{fontSize:12,color:P.muted,display:'block',marginBottom:4,letterSpacing:1,textTransform:'uppercase'}}>Maestro</label>
              <input type="text" value={dbForm.maestro} onChange={f('maestro')}
                placeholder="es: ANTONELLO"
                readOnly={!!dbForm.corsoId}
                style={{width:'100%',background:P.bg,border:`1px solid ${P.border}`,borderRadius:8,padding:'8px 10px',color:dbForm.corsoId?P.muted:P.text,fontSize:15,cursor:dbForm.corsoId?'default':'text'}}/>
              {dbForm.corsoId && (
                <div style={{fontSize:12,color:P.muted,marginTop:3}}>Impostato automaticamente dal corso</div>
              )}
            </div>

            {[
              {k:'nome',l:'Nome',ph:'Mario'},
              {k:'cognome',l:'Cognome',ph:'Rossi'},
              {k:'telefono',l:'Telefono',ph:'+39 333 ...'},
              {k:'email',l:'Email',ph:'mario@email.com'},
              {k:'data_nascita',l:'Data di nascita',t:'date'},
              {k:'retta',l:'Retta mensile (€)',t:'number',ph:'120'},
              {k:'note',l:'Note',ph:'...'},
            ].map(({k,l,t='text',ph=''})=>(
              <div key={k} style={{marginBottom:12}}>
                <label style={{fontSize:12,color:P.muted,display:'block',marginBottom:4,letterSpacing:1,textTransform:'uppercase'}}>{l}</label>
                <input type={t} value={dbForm[k]||''} onChange={f(k)} placeholder={ph}
                  style={{width:'100%',background:P.bg,border:`1px solid ${P.border}`,borderRadius:8,padding:'8px 10px',color:P.text,fontSize:15}}/>
              </div>
            ))}

            <div style={{display:'flex',gap:8,marginTop:18}}>
              <button onClick={()=>setShowModal(false)} style={{flex:1,padding:'9px',borderRadius:8,border:`1px solid ${P.border}`,background:'transparent',color:P.muted,cursor:'pointer',fontSize:15}}>Annulla</button>
              <button onClick={save} style={{flex:1,padding:'9px',borderRadius:8,border:'none',background:P.gold,color:'#fff',cursor:'pointer',fontWeight:600,fontSize:15}}>Salva</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
